import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, format } from 'date-fns';

interface StatusDurationStats {
  status: string;
  avg_duration_hours: number;
  min_duration_hours: number;
  max_duration_hours: number;
  total_occurrences: number;
}

interface RawDurationStats {
  status_novo: string;
  avg_duration: string;
  min_duration: string;
  max_duration: string;
  count_transitions: number;
}

export const useStatusMetrics = (startDate?: Date, endDate?: Date) => {
  const start = startDate || startOfMonth(new Date());
  const end = endDate || endOfMonth(new Date());

  return useQuery({
    queryKey: ['status-metrics', format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_status_duration_stats', {
        p_start_date: format(start, 'yyyy-MM-dd'),
        p_end_date: format(end, 'yyyy-MM-dd')
      });

      if (error) throw error;

      // Transform raw data to expected format
      const rawData = data as unknown as RawDurationStats[];
      return rawData.map(item => {
        // Parse intervals like "01:23:45" to hours
        const parseInterval = (interval: string): number => {
          if (!interval) return 0;
          const parts = interval.split(':');
          return parseFloat(parts[0]) + parseFloat(parts[1]) / 60 + parseFloat(parts[2]) / 3600;
        };

        return {
          status: item.status_novo,
          avg_duration_hours: parseInterval(item.avg_duration),
          min_duration_hours: parseInterval(item.min_duration),
          max_duration_hours: parseInterval(item.max_duration),
          total_occurrences: item.count_transitions
        };
      });
    },
    staleTime: 5 * 60 * 1000,
  });
};
